import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HubOption } from 'dorian-shared/types/branch/HubOption';
import type { ReplayCondition } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { createDefaultReplayCondition } from '../hubStepDefaults';

type OnChange = (changes: Partial<HubOption>) => void;

export function useReplayConditions(
  replayConditions: ReplayCondition[],
  onChange: OnChange,
  memoryVariables: MemoryDTO[],
) {
  const addCondition = useCallback(() => {
    const newCondition = createDefaultReplayCondition();
    onChange({ replayConditions: [...replayConditions, newCondition] });
  }, [replayConditions, onChange]);

  const removeCondition = useCallback(
    (uuid: string) => {
      onChange({
        replayConditions: replayConditions.filter((c) => c.uuid !== uuid),
      });
    },
    [replayConditions, onChange],
  );

  const updateCondition = useCallback(
    (uuid: string, updated: ReplayCondition) => {
      onChange({
        replayConditions: replayConditions.map((c) =>
          c.uuid === uuid ? updated : c,
        ),
      });
    },
    [replayConditions, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = replayConditions.findIndex((c) => c.uuid === active.id);
        const newIndex = replayConditions.findIndex((c) => c.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onChange({ replayConditions: arrayMove(replayConditions, oldIndex, newIndex) });
        }
      }
    },
    [replayConditions, onChange],
  );

  const summary = useMemo((): string => {
    if (replayConditions.length === 0) return '';

    const first = replayConditions[0];
    const stateLabel = first.resultState?.toUpperCase();

    const varName = (id: number | null) => {
      if (id == null) return '...';
      const v = memoryVariables.find((m) => m.id === id);
      return v?.displayName || v?.name || `#${id}`;
    };

    const subLabels = first.subConditions
      .map((s) => varName(s.variableId))
      .join(', ');

    let text = stateLabel
      ? `${stateLabel} when ${subLabels}`
      : `override when ${subLabels}`;

    if (replayConditions.length > 1) {
      text += ` + ${replayConditions.length - 1} more`;
    }

    return text;
  }, [replayConditions, memoryVariables]);

  return {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
    summary,
  };
}
